import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, MapPin, Menu, MessageCircle, Phone, X } from "lucide-react";
import Logo, { LogoMark } from "./Logo";
import { LocationWhatsAppButton } from "./WhatsAppCTA";
import { BUSINESS, WA_DEFAULT, WA_ORDER } from "@/data/business";
import { SERVICES } from "@/data/services";
import { AREAS } from "@/data/areas";

const NAV = [
  { label: "الرئيسية", to: "/" },
  { label: "خدماتنا", to: "/#services" },
  { label: "مناطقنا", to: "/#areas" },
  { label: "المدونة", to: "/المدونة" },
];

export const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  if (hash) {
    setTimeout(() => {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo(0, 0);
    }, 60);
  } else {
    window.scrollTo(0, 0);
  }
  return null;
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-ink/85 backdrop-blur-xl border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" aria-label="القائمة الرئيسية">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`nav-${n.label}`}
              className="text-sm font-medium text-gray-300 hover:text-gold-soft transition-colors duration-300"
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <a
            href={BUSINESS.telLink}
            data-testid="navbar-phone-link"
            className="hidden sm:flex items-center gap-2 text-sm font-bold text-white border border-edge hover:border-gold/60 rounded-full px-4 py-2 transition-colors duration-300"
          >
            <Phone size={15} className="text-gold" />
            <span dir="ltr" className="font-mono">{BUSINESS.phoneDisplay}</span>
          </a>
          <a
            href={WA_ORDER}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-order-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-ink font-extrabold text-sm rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <MessageCircle size={15} />
            اطلب بنشر متنقل
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="navbar-menu-toggle"
            aria-label="القائمة"
            className="lg:hidden p-2 text-white border border-edge rounded-lg"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-ink border-b border-gold/20"
            aria-label="قائمة الجوال"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.label}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="py-3 text-gray-200 font-medium border-b border-edge/60 last:border-0"
                >
                  {n.label}
                </Link>
              ))}
              <a
                href={BUSINESS.telLink}
                onClick={() => setOpen(false)}
                className="py-3 flex items-center gap-2 text-white font-bold"
              >
                <Phone size={16} className="text-gold" />
                اتصل الآن — <span dir="ltr">{BUSINESS.phoneDisplay}</span>
              </a>
              <a
                href={WA_ORDER}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="py-3 flex items-center gap-2 text-wa font-bold"
              >
                <MessageCircle size={16} />
                اطلب عبر واتساب
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const MobileCTABar = () => (
  <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 bg-ink/95 backdrop-blur-2xl border-t border-gold/30 px-2.5 py-2 shadow-[0_-10px_30px_rgba(0,0,0,0.8)] grid grid-cols-3 gap-2">
    <a
      href={BUSINESS.telLink}
      data-testid="mobile-bar-call"
      className="flex flex-col items-center justify-center gap-0.5 bg-blood hover:bg-blood-deep text-white font-bold text-xs rounded-xl py-2.5 min-h-[44px] transition-colors"
    >
      <Phone size={17} />
      اتصل الآن
    </a>
    <a
      href={WA_ORDER}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="mobile-bar-whatsapp"
      className="flex flex-col items-center justify-center gap-0.5 bg-wa hover:bg-wa-deep text-white font-bold text-xs rounded-xl py-2.5 min-h-[44px] transition-colors"
    >
      <MessageCircle size={17} />
      واتساب
    </a>
    <LocationWhatsAppButton
      testid="mobile-bar-location"
      label="أرسل موقعك"
      className="flex flex-col items-center justify-center gap-0.5 bg-gold hover:bg-gold-deep text-ink font-extrabold text-xs rounded-xl py-2.5 min-h-[44px] transition-colors"
    />
  </div>
);

const Footer = () => (
  <footer className="bg-panel border-t border-gold/20 mt-0">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <LogoMark className="h-9 w-9" />
          <span className="font-display font-black text-white text-lg">بنشر متنقل نجم الإطار</span>
        </div>
        <p className="text-sm text-gray-400 leading-relaxed mb-4">
          خدمة بنشر متنقل متخصصة في الرياض: إصلاح البنشر، تغيير الكفرات، تركيب الإطارات والجنوط، برمجة حساسات TPMS — نصل إليك أينما كنت.
        </p>
        <div className="flex items-center gap-2 text-sm text-gold-soft font-semibold">
          <Clock size={15} />
          {BUSINESS.hours}
        </div>
      </div>
      <nav aria-label="خدماتنا">
        <h3 className="font-display font-bold text-white mb-4">خدماتنا</h3>
        <ul className="space-y-2.5">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link to={`/${s.slug}`} className="text-sm text-gray-400 hover:text-gold-soft transition-colors">
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <nav aria-label="مناطق الخدمة">
        <h3 className="font-display font-bold text-white mb-4">مناطق الخدمة</h3>
        <ul className="space-y-2.5">
          {AREAS.map((a) => (
            <li key={a.slug}>
              <Link to={`/${a.slug}`} className="text-sm text-gray-400 hover:text-gold-soft transition-colors">
                بنشر متنقل {a.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div>
        <h3 className="font-display font-bold text-white mb-4">التواصل</h3>
        <ul className="space-y-3 text-sm">
          <li>
            <a
              href={BUSINESS.telLink}
              data-testid="footer-phone"
              className="flex items-center gap-2 text-white font-bold hover:text-gold-soft transition-colors"
            >
              <Phone size={15} className="text-gold" />
              <span dir="ltr" className="font-mono">{BUSINESS.phoneDisplay}</span>
            </a>
          </li>
          <li>
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="flex items-center gap-2 text-gray-300 hover:text-wa transition-colors"
            >
              <MessageCircle size={15} className="text-wa" />
              واتساب مباشر
            </a>
          </li>
          <li className="text-gray-400 text-xs leading-relaxed">
            اتصل أو راسلنا واتساب — نرد على مدار الساعة طوال أيام الأسبوع
          </li>
          <li className="text-gray-500 pt-2">الرياض، المملكة العربية السعودية</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-edge">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
        <span>© 2026 بنشر متنقل نجم الإطار — جميع الحقوق محفوظة</span>
        <span>Najm Al Etar Mobile Tire Service — Riyadh</span>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="App min-h-screen bg-ink">
      <Navbar />
      <main>{children}</main>
      <Footer />
      <MobileCTABar />
    </div>
  );
}
